import { uniq } from 'es-toolkit';
import React, { useState } from 'react';
import DeleteIcon from '@mui/icons-material/Delete';
import {
  Autocomplete,
  Box,
  Button,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  Tab,
  TableCell,
  TableRow,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import { Stack } from '@mui/system';

import { PropertyTextInput } from '@paragon/client-tools';
import { NewTrussGroup, TrussGroup } from '@paragon/contract-library';
import { InlineNotification } from '@paragon/notification-tools';
import { naturalSortByProperty } from '@paragon/utilities';

import { selectItems } from '../../../store/actions/selectActions';
import { getPickingObjectInfo } from '../../../store/selectors/pickingSelectors';
import { getAllTrussEnvelopeGuidsRecursive } from '../../../store/selectors/trussGroups';
import {
  applicationOnStartPickingTrussEnvelopesForTrussGroup,
  clearPickingObject,
} from '../../../store/slices/applicationSlice';
import { useAppDispatch, useAppSelector } from '../../hooks';
import { PropertyTable, zebraStripingSx } from './properties/PropertyTable';

export function getUpdatedTrussEnvelopeGuidsForTrussGroup(
  currentTrussEnvelopeGuids: readonly string[],
  trussEnvelopeGuidsToAdd: readonly string[],
  trussEnvelopeGuidsToRemove: readonly string[],
) {
  return uniq(
    currentTrussEnvelopeGuids
      .filter((trussEnvelopeGuid) => !trussEnvelopeGuidsToRemove.includes(trussEnvelopeGuid))
      .concat(trussEnvelopeGuidsToAdd),
  );
}

export interface TrussGroupPropertiesProps {
  readonly trussGroup: TrussGroup | NewTrussGroup;
  readonly onChange: (updatedTrussGroup: Partial<Omit<TrussGroup, 'guid'>>) => void;
  readonly otherTrussGroups: { [guid: string]: TrussGroup };
  readonly inserting?: boolean;
  readonly disabled?: boolean;
}

export function TrussGroupProperties({
  trussGroup,
  onChange,
  otherTrussGroups,
  inserting,
  disabled,
}: TrussGroupPropertiesProps) {
  const dispatch = useAppDispatch();

  const trussEnvelopes = useAppSelector((state) => state.layout.trussEnvelopes);
  const componentDesigns = useAppSelector((state) => state.componentDesigns.data);
  const pickingObjectInfo = useAppSelector(getPickingObjectInfo);
  const isPickingTrussEnvelopes = pickingObjectInfo?.type === 'pickingTrussEnvelopesForTrussGroup';

  const [activeTab, setActiveTab] = useState<'truss-envelopes' | 'component-designs'>('truss-envelopes');

  const trussesBeingAddedFragment =
    isPickingTrussEnvelopes && pickingObjectInfo.trussEnvelopeGuidsToAdd.length > 0
      ? `Add ${pickingObjectInfo.trussEnvelopeGuidsToAdd.length} Truss Envelope${pickingObjectInfo.trussEnvelopeGuidsToAdd.length !== 1 ? 's' : ''}`
      : '';
  const bothAddingAndRemovingFragment =
    isPickingTrussEnvelopes &&
    pickingObjectInfo.trussEnvelopeGuidsToAdd.length > 0 &&
    pickingObjectInfo.trussEnvelopeGuidsToRemove.length > 0
      ? ', '
      : '';
  const trussesBeingRemovedFragment =
    isPickingTrussEnvelopes && pickingObjectInfo.trussEnvelopeGuidsToRemove.length > 0
      ? `Remove ${pickingObjectInfo.trussEnvelopeGuidsToRemove.length} Truss Envelope${pickingObjectInfo.trussEnvelopeGuidsToRemove.length !== 1 ? 's' : ''}`
      : '';
  const pickButtonLabel = isPickingTrussEnvelopes
    ? pickingObjectInfo.trussEnvelopeGuidsToAdd.length === 0 &&
      pickingObjectInfo.trussEnvelopeGuidsToRemove.length === 0
      ? 'Cancel Picking'
      : `${trussesBeingAddedFragment}${bothAddingAndRemovingFragment}${trussesBeingRemovedFragment}`
    : 'Modify Truss Group';

  const trussGroupGuid = 'guid' in trussGroup ? trussGroup.guid : null;

  const selectTheseTrussEnvelopes =
    trussGroupGuid !== null
      ? getAllTrussEnvelopeGuidsRecursive([trussGroupGuid], {
          ...otherTrussGroups,
          [trussGroupGuid]: trussGroup as TrussGroup,
        }).map((guid) => trussEnvelopes[guid])
      : [];

  const trussEnvelopeGuidsFromOtherTrussGroups = Object.values(otherTrussGroups).flatMap(
    (trussGroup) => trussGroup.trussEnvelopeGuids,
  );
  const trussEnvelopeGuidsIncludingPickingChanges = isPickingTrussEnvelopes
    ? getUpdatedTrussEnvelopeGuidsForTrussGroup(
        trussGroup.trussEnvelopeGuids,
        pickingObjectInfo.trussEnvelopeGuidsToAdd,
        pickingObjectInfo.trussEnvelopeGuidsToRemove,
      )
    : trussGroup.trussEnvelopeGuids;
  const trussEnvelopesWhichExistInAnotherTrussGroup = naturalSortByProperty(
    trussEnvelopeGuidsIncludingPickingChanges
      .filter((guid) => trussEnvelopeGuidsFromOtherTrussGroups.includes(guid))
      .map((guid) => trussEnvelopes[guid]?.name ?? 'Loading...'),
    (name) => name,
  );

  const parentOptionsWhichWillNotCauseCycles = naturalSortByProperty(
    Object.values(otherTrussGroups).filter((otherTrussGroup) => {
      let currentParentGuid = otherTrussGroup.parentGuid;
      while (currentParentGuid != null) {
        if (trussGroupGuid !== null && currentParentGuid === trussGroupGuid) return false;
        currentParentGuid = otherTrussGroups[currentParentGuid]?.parentGuid ?? null;
      }
      return true;
    }),
    (trussGroup) => trussGroup.name,
  );

  let undesignedCount = 0;
  const componentDesignsForTrussGroup: {
    [componentDesignGuid: string]: { readonly componentDesignName: string; quantity: number };
  } = {};
  for (const trussEnvelopeGuid of trussEnvelopeGuidsIncludingPickingChanges) {
    const trussEnvelope = trussEnvelopes[trussEnvelopeGuid];
    if (trussEnvelope) {
      if (trussEnvelope.componentDesignGuid) {
        if (!(trussEnvelope.componentDesignGuid in componentDesignsForTrussGroup)) {
          componentDesignsForTrussGroup[trussEnvelope.componentDesignGuid] = {
            componentDesignName: componentDesigns[trussEnvelope.componentDesignGuid]?.component.name ?? 'Loading...',
            quantity: 0,
          };
        }
        componentDesignsForTrussGroup[trussEnvelope.componentDesignGuid].quantity += 1;
      } else {
        undesignedCount += 1;
      }
    }
  }

  return (
    <Stack>
      <PropertyTextInput
        property="Truss Group Name"
        value={trussGroup.name}
        submit={(newName) => void onChange({ name: newName })}
        disabled={disabled}
        fullWidth
      />
      {inserting &&
        Object.values(otherTrussGroups).some((otherTrussGroup) => otherTrussGroup.name === trussGroup.name) && (
          <InlineNotification type="warning">
            Another Truss Group with this name exists in the project.
          </InlineNotification>
        )}

      <Autocomplete
        value={trussGroup.parentGuid ? otherTrussGroups[trussGroup.parentGuid] : null}
        onChange={(_, newParentTrussGroup) => void onChange({ parentGuid: newParentTrussGroup?.guid ?? null })}
        options={parentOptionsWhichWillNotCauseCycles}
        getOptionLabel={(trussGroup) => trussGroup.name}
        getOptionKey={(trussGroup) => trussGroup.guid}
        renderInput={(props) => <TextField {...props} label="Parent Truss Group" />}
        disabled={disabled || parentOptionsWhichWillNotCauseCycles.length === 0}
        size="small"
      />

      <Box sx={{ display: 'flex', justifyContent: 'center', marginBottom: 1 }}>
        <Tabs value={activeTab} onChange={(_, activeTab) => void setActiveTab(activeTab)}>
          <Tab value="truss-envelopes" label="Truss Envelopes" />
          <Tab value="component-designs" label="Component Designs" disabled={isPickingTrussEnvelopes} />
        </Tabs>
      </Box>

      {activeTab === 'truss-envelopes' && (
        <Stack spacing={2}>
          <Button
            variant={isPickingTrussEnvelopes ? 'contained' : 'outlined'}
            onClick={() => {
              if (isPickingTrussEnvelopes) {
                if (
                  pickingObjectInfo.trussEnvelopeGuidsToAdd.length > 0 ||
                  pickingObjectInfo.trussEnvelopeGuidsToRemove.length > 0
                ) {
                  onChange({
                    trussEnvelopeGuids: trussEnvelopeGuidsIncludingPickingChanges,
                  });
                }
                dispatch(clearPickingObject());
              } else
                dispatch(
                  applicationOnStartPickingTrussEnvelopesForTrussGroup({
                    trussGroupGuid,
                  }),
                );
            }}
            disabled={disabled}
          >
            {pickButtonLabel}
          </Button>
          {!inserting && (
            <Button
              variant="outlined"
              onClick={() => void dispatch(selectItems(selectTheseTrussEnvelopes, 'trussEnvelope'))}
              disabled={isPickingTrussEnvelopes || selectTheseTrussEnvelopes.length === 0}
            >
              Select These Truss Envelopes
            </Button>
          )}
          {trussEnvelopesWhichExistInAnotherTrussGroup.length > 0 && (
            <InlineNotification type="info" sx={{ textWrapMode: 'wrap' }}>
              These Truss Envelopes also exist in other Truss Groups:{' '}
              {trussEnvelopesWhichExistInAnotherTrussGroup.join(', ')}
            </InlineNotification>
          )}
          <Typography sx={{ textAlign: 'center' }}>
            {trussEnvelopeGuidsIncludingPickingChanges.length} Included Truss Envelope
            {trussEnvelopeGuidsIncludingPickingChanges.length !== 1 ? 's' : ''}
            {isPickingTrussEnvelopes && ' (includes pending changes)'}
          </Typography>
          <List>
            {naturalSortByProperty(
              trussEnvelopeGuidsIncludingPickingChanges,
              (guid) => trussEnvelopes[guid]?.name ?? '',
            ).map((trussEnvelopeGuid) => {
              const trussEnvelopeName = trussEnvelopes[trussEnvelopeGuid]?.name ?? 'Loading...';
              const componentDesignName = trussEnvelopes[trussEnvelopeGuid]
                ? trussEnvelopes[trussEnvelopeGuid].componentDesignGuid
                  ? componentDesigns[trussEnvelopes[trussEnvelopeGuid].componentDesignGuid].component.name
                  : null
                : 'Loading...';
              return (
                <ListItem
                  key={trussEnvelopeGuid}
                  secondaryAction={
                    <IconButton
                      size="small"
                      onClick={() =>
                        void onChange({
                          trussEnvelopeGuids: trussGroup.trussEnvelopeGuids.filter(
                            (guid) => guid !== trussEnvelopeGuid,
                          ),
                        })
                      }
                      edge="end"
                      disabled={disabled || isPickingTrussEnvelopes}
                    >
                      <DeleteIcon />
                    </IconButton>
                  }
                  disablePadding
                >
                  <ListItemButton>
                    <Typography sx={{ verticalAlign: 'middle' }}>
                      {trussEnvelopeName}{' '}
                      <Typography component="span" sx={{ color: 'text.secondary' }}>
                        {componentDesignName ? `(Component Design: ${componentDesignName})` : '(undesigned)'}
                      </Typography>
                    </Typography>
                  </ListItemButton>
                </ListItem>
              );
            })}
          </List>
        </Stack>
      )}

      {activeTab === 'component-designs' &&
        (Object.values(componentDesignsForTrussGroup).length > 0 ? (
          <PropertyTable columns={['Name', 'Quantity']}>
            {undesignedCount > 0 && (
              <TableRow>
                <TableCell>
                  <Typography>(Undesigned)</Typography>
                </TableCell>
                <TableCell>
                  <Typography>{undesignedCount}</Typography>
                </TableCell>
              </TableRow>
            )}
            {naturalSortByProperty(
              Object.values(componentDesignsForTrussGroup),
              (nameAndQuantity) => nameAndQuantity.componentDesignName,
            ).map(({ componentDesignName, quantity }) => (
              <TableRow key={componentDesignName} sx={zebraStripingSx}>
                <TableCell>
                  <Typography>{componentDesignName}</Typography>
                </TableCell>
                <TableCell>
                  <Typography>{quantity}</Typography>
                </TableCell>
              </TableRow>
            ))}
          </PropertyTable>
        ) : (
          <Typography>No Component Designs</Typography>
        ))}
    </Stack>
  );
}
